import { Paper, Grid } from "@mui/material";
import { styled } from '@mui/material/styles';
import * as React from "react";
import EntityId from "../../core/api/EntityId";
import InvoiceModel from "../../core/api/invoice/InvoiceModel";
import ProformaInvoiceModel from "../../core/api/invoice/ProformaInvoiceModel";
import { ReminderData, defaultReminderData, ReminderModel } from "../../core/api/reminder/ReminderModel";
import InvoiceEmailModalForm from "../email/InvoiceEmailModalForm";
import InvoiceModalForm from "../invoice/InvoiceModalForm";
import InvoiceModalView from "../invoice/InvoiceModalView";
import ProformaInvoiceModalForm from "../invoice/ProformaInvoiceModalForm";
import ReminderModalForm from "../reminder/ReminderModalForm";
import { ActionButton } from "./components/ActionButton";
import { useLastProformaDate } from "./hooks/useLastProformaDate";
import { useModalState } from "./hooks/useModalState";
import { ClientControlsProps, EmailState } from "./types";
import { translations } from "../../translations/common";

const ControlsContainer = styled(Grid)(({ theme }) => ({
  margin: theme.spacing(0),
  height: "100%",
  width: "100%",
}));

const StyledPaper = styled(Paper)({
  height: "100%",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  boxShadow: "none",
  border: "none",
  padding: 0,
});

const CenteredActionButton = styled(ActionButton)({
  width: "100%",
  justifyContent: "center",
  textAlign: "center",
});

/**
 * Component that displays and manages client control actions and their associated modals
 */
export const ClientControls: React.FC<ClientControlsProps> = ({ clientId, tableRef }) => {
  const { state, updateState, resetEmail } = useModalState({
    showReminder: false,
    showSia: false,
    newSia: false,
    newInvoice: false,
    email: null,
  });

  const lastSentDate = useLastProformaDate(clientId, state.email);

  const handleSendProforma = React.useCallback(() => {
    const emailState: EmailState = {
      title: translations.clientControls.sendProforma.title,
      pdfUrl: () => ProformaInvoiceModel.getPdf(clientId),
      send: async (content) => {
        const id = await ProformaInvoiceModel.send(content, clientId);
        return { id } as EntityId;
      },
    };
    updateState('email', emailState);
  }, [clientId, updateState]);

  const handleInvoiceSuccess = React.useCallback((id: string) => {
    const emailState: EmailState = {
      title: translations.clientControls.sendInvoice.title(id),
      pdfUrl: () => InvoiceModel.getPdf(id),
      send: async (content) => {
        const response = await InvoiceModel.send(content, id);
        return response;
      },
    };
    updateState('email', emailState);
  }, [updateState]);

  const handleCloseModal = React.useCallback((modalKey: keyof typeof state) => {
    updateState(modalKey, false);
  }, [updateState]);

  return (
    <StyledPaper>
      <ControlsContainer
        container
        alignItems="center"
        justifyContent="center"
        direction="column"
        spacing={3}
      >
        <Grid size={12} style={{ width: "100%", textAlign: "center" }}>
          <CenteredActionButton onClick={() => updateState('newSia', true)}>
            {translations.clientControls.newProforma}
          </CenteredActionButton>
        </Grid>

        {lastSentDate && (
          <Grid size={12} style={{ width: "100%", textAlign: "center" }}>
            <CenteredActionButton onClick={() => updateState('showSia', true)}>
              {translations.clientControls.lastProforma(lastSentDate)}
            </CenteredActionButton>
          </Grid>
        )}

        <Grid size={12} style={{ width: "100%", textAlign: "center" }}>
          <CenteredActionButton onClick={() => updateState('showReminder', true)}>
            {translations.clientControls.newReminder}
          </CenteredActionButton>
        </Grid>

        <Grid size={12} style={{ width: "100%", textAlign: "center" }}>
          <CenteredActionButton onClick={() => updateState('newInvoice', true)}>
            {translations.clientControls.newInvoice}
          </CenteredActionButton>
        </Grid>

        {state.showReminder && (
          <ReminderModalForm
            value={defaultReminderData}
            save={(values: ReminderData) => ReminderModel.updateOrCreate(clientId, values)}
            close={() => handleCloseModal('showReminder')}
          />
        )}

        {state.showSia && (
          <InvoiceModalView
            close={() => handleCloseModal('showSia')}
            pdfUrl={() => ProformaInvoiceModel.getPdf(clientId)}
            sendAction={() => {
              handleCloseModal('showSia');
              handleSendProforma();
            }}
          />
        )}

        {state.email && (
          <InvoiceEmailModalForm
            clientId={clientId}
            title={state.email.title}
            close={resetEmail}
            pdfUrl={state.email.pdfUrl}
            onSubmit={state.email.send}
          />
        )}

        {state.newInvoice && (
          <InvoiceModalForm
            close={() => handleCloseModal('newInvoice')}
            clientId={clientId}
            tableRef={tableRef}
            onSuccess={handleInvoiceSuccess}
          />
        )}

        {state.newSia && (
          <ProformaInvoiceModalForm
            onSuccess={handleSendProforma}
            close={() => handleCloseModal('newSia')}
            clientId={clientId}
          />
        )}
      </ControlsContainer>
    </StyledPaper>
  );
};
